import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useLanguage, localizedPath } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { thumbUrl, srcSet } from "@/lib/images";
import { ART_LABEL, ART_SUB } from "@/lib/categories";

// Beschriftungen und Reihenfolge der Rubriken kommen aus lib/categories
// (mit der Startseite geteilt)

export default function Projects() {
  const [sections, setSections] = useState([]);
  const [searchParams] = useSearchParams();
  // Rücksprung aus der Rubrik-Slideshow öffnet dieselbe Rubrik wieder
  const [openArt, setOpenArt] = useState(() => searchParams.get("rubrik"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { t, lang } = useLanguage();
  useSeo({
    title: "Projekte · Wim Jansen Delgado · Fotograf Hamburg",
    description:
      "Ausgewählte Projekte des Fotografen Wim Jansen Delgado: Restaurants, Hotels und Reisen, kuratiert als Serien.",
    path: "/projekte",
  });

  // Ein erneuter Versuch nach 1,5 s, danach ruhige Fehlermeldung mit Retry
  const load = (attempt = 0) => {
    setLoading(true);
    api
      .get("/overview-grid")
      .then((res) => {
        setSections(res.data);
        setError(false);
        setLoading(false);
      })
      .catch(() => {
        if (attempt < 1) {
          setTimeout(() => load(attempt + 1), 1500);
        } else {
          setSections([]);
          setError(true);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    load();
  }, []);

  // Rücksprung aus der Rubrik-Slideshow: zur zuletzt gesehenen Kachel scrollen
  useEffect(() => {
    if (sections.length === 0 || !openArt) return;
    const b = parseInt(searchParams.get("bild") || "", 10);
    if (!b) return;
    const el = document.querySelector(`[data-testid="overview-thumb-${openArt}-${b}"]`);
    if (el) el.scrollIntoView({ block: "center" });
  }, [sections, openArt, searchParams]);

  // Beim Überfahren einer Rubrik schon die ersten Thumbs still laden,
  // damit das Grid beim Klick nahezu sofort dasteht
  const prefetched = useRef(new Set());
  const prefetchArt = (sec) => {
    if (prefetched.current.has(sec.art)) return;
    prefetched.current.add(sec.art);
    sec.images.slice(0, 12).forEach((img) => {
      const im = new Image();
      im.src = thumbUrl(img.image, 400);
    });
  };

  return (
    <div data-testid="projects-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-28 md:px-12 md:pt-36 lg:px-16">
        <Reveal>
          <p className="mb-4 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
            {t("Ausgewählte Serien")}
          </p>
          {/* vorübergehender Hinweis als handgemachter Stempel */}
          <div
            data-testid="projects-update-note"
            className="mb-10 inline-block -rotate-[4deg] rounded-sm border-[3px] border-ember-deep/70 px-5 py-2.5 shadow-[inset_0_0_0_1px_rgba(181,64,31,0.25)]"
            style={{ fontVariant: "small-caps" }}
          >
            <p className="font-mono text-sm font-medium uppercase tracking-[0.22em] text-ember-deep/90">
              {t("Wird gerade neu gestaltet")}
            </p>
          </div>
        </Reveal>
        <h1 data-testid="projects-title" className="sr-only">
          {t("Projekte")}
        </h1>

        {loading ? (
          <div className="mt-16 space-y-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 w-2/3 animate-pulse bg-panel" />
            ))}
          </div>
        ) : error ? (
          <p className="mt-16 font-mono text-sm font-light text-smoke" data-testid="projects-error">
            {t("Inhalte konnten nicht geladen werden.")}{" "}
            <button
              type="button"
              onClick={() => load(0)}
              data-testid="projects-retry"
              className="underline decoration-ember/60 underline-offset-4 transition-colors hover:text-ember-deep"
            >
              {t("Erneut versuchen")}
            </button>
          </p>
        ) : sections.length === 0 ? (
          <p className="mt-16 font-mono text-sm font-light text-smoke" data-testid="projects-empty">
            {t("Noch keine Bilder. Sie erscheinen, sobald Serien eine Rubrik tragen.")}
          </p>
        ) : (
          <div
            className="mt-10 flex min-h-[55vh] flex-col items-center justify-center md:mt-14"
            data-testid="projects-list"
          >
            {/* Rubriken untereinander, mittig auf der Seite — Klick öffnet
                die Bilder direkt darunter; erneuter Klick schließt wieder */}
            {sections.map((sec) => (
              <div key={sec.art} className="flex w-full flex-col items-center">
                <button
                  type="button"
                  onClick={() => setOpenArt(openArt === sec.art ? null : sec.art)}
                  onMouseEnter={() => prefetchArt(sec)}
                  onFocus={() => prefetchArt(sec)}
                  data-testid={`overview-cat-${sec.art}`}
                  aria-expanded={openArt === sec.art}
                  className="group py-6 md:py-8"
                >
                  <span className="flex flex-col items-center gap-y-3">
                    <span
                      className={`font-serif text-4xl font-light leading-none tracking-tight transition-colors duration-300 group-hover:text-ember sm:text-5xl md:text-7xl ${
                        openArt === sec.art ? "text-ember" : "text-bone"
                      }`}
                    >
                      {t(ART_LABEL[sec.art] || sec.art)}
                    </span>
                    <span
                      data-testid={`overview-cat-sub-${sec.art}`}
                      className="font-mono text-xs font-light uppercase tracking-[0.2em] text-smoke"
                    >
                      {t(ART_SUB[sec.art] || "")}
                    </span>
                  </span>
                </button>
                {openArt === sec.art && (
                  <div
                    className="group/grid mb-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-8 md:gap-x-4"
                    data-testid={`overview-cat-grid-${sec.art}`}
                  >
                    {sec.images.map((img, i) => {
                      const land = img.orientation === "landscape";
                      return (
                        <Link
                          key={`${img.slug}-${img.bild}`}
                          to={`${localizedPath(`/projekte/rubrik/${sec.art}`, lang)}?bild=${i + 1}`}
                          data-testid={`overview-thumb-${sec.art}-${i + 1}`}
                          className={`group relative block ${
                            land
                              ? "w-[calc(50%-0.75rem)] md:w-[calc(23.333%-0.84rem)]"
                              : "w-[calc(50%-0.75rem)] md:w-[calc(16.666%-0.84rem)]"
                          }`}
                        >
                          <img
                            src={thumbUrl(img.image, 400)}
                            srcSet={srcSet(img.image, [400, 800])}
                            sizes={land ? "(max-width: 768px) 50vw, 23vw" : "(max-width: 768px) 50vw, 17vw"}
                            width={land ? 1120 : 800}
                            height={land ? 800 : 1120}
                            alt={(lang === "en" ? img.alt_en : img.alt_de) || img.title || t("Bild")}
                            loading="lazy"
                            decoding="async"
                            className="w-full"
                          />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
